import { useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  TextField,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { CheckIcon, UnCheckIcon } from '@/components/icons'
import ButtonField from '@/components/form-control/ButtonField'
import { useSavedViewsContext } from '@/features/config/contexts/saved-views-context'
import type { GridColumnState } from '@/components/grid/types'
import useTranslation from '@/hooks/useTranslation'

interface ColumnItem {
  field: string
  headerName: string
  [key: string]: any
}

interface ColumnSettingPopoverProps {
  columns: ColumnItem[]
  anchorEl: HTMLElement | null
  onClose: () => void
  visibleColumns: Record<string, boolean>
  onToggleColumn: (field: string) => void
  onShowAll: () => void
  onHideAll: () => void
  alwaysVisibleFields?: string[]
  excludeFields?: string[]
  /**
   * The grid's live column state, read when a view is saved. Taken from the
   * grid rather than rebuilt from `visibleColumns` so a view records the
   * order and widths the user actually arranged, not just the checkboxes.
   */
  getColumnState: () => GridColumnState[]
  // Optional one-click presets that show a related group of columns.
  presets?: { name: string; fields: string[] }[]
  onApplyPreset?: (fields: string[]) => void
}

const ColumnSettingPopover = ({
  columns,
  anchorEl,
  onClose,
  visibleColumns,
  onToggleColumn,
  onShowAll,
  onHideAll,
  alwaysVisibleFields = [],
  excludeFields = [],
  getColumnState,
  presets = [],
  onApplyPreset,
}: ColumnSettingPopoverProps) => {
  const { saveView, canPersist } = useSavedViewsContext()
  const { t } = useTranslation()
  const [showSaveInput, setShowSaveInput] = useState(false)
  const [viewName, setViewName] = useState('')

  const handleSaveConfirm = () => {
    const trimmed = viewName.trim()
    if (!trimmed) return
    saveView(trimmed, getColumnState())
    setViewName('')
    setShowSaveInput(false)
  }

  const filteredColumns = columns.filter(
    (col) => !excludeFields.includes(col.field),
  )

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 250,
            maxWidth: 350,
            maxHeight: 'calc(100vh - 120px)',
            overflow: 'hidden',
            borderRadius: '4px',
            boxShadow:
              '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: 'calc(100vh - 120px)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ fontSize: '13px', mb: 1 }}
          >
            {t('commonColumnSetting')}
          </Typography>

          {/* Presets — one click to show a related group of columns */}
          {presets.length > 0 && onApplyPreset && (
            <Box sx={{ mb: 1.25 }}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'grey.600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  mb: 0.5,
                }}
              >
                {t('commonPresets')}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {presets.map((preset) => (
                  <Box
                    key={preset.name}
                    component="button"
                    type="button"
                    onClick={() => onApplyPreset(preset.fields)}
                    sx={{
                      cursor: 'pointer',
                      height: 26,
                      px: 1.25,
                      borderRadius: '13px',
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: 1,
                      color: '#004744',
                      backgroundColor: 'rgba(0, 71, 68, 0.07)',
                      border: '1px solid rgba(0, 71, 68, 0.25)',
                      transition:
                        'background-color 0.15s ease, border-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 71, 68, 0.14)',
                        borderColor: 'rgba(0, 71, 68, 0.5)',
                      },
                      '[data-theme="dark"] &': {
                        color: '#5fe0ad',
                        backgroundColor: 'rgba(16, 185, 129, 0.14)',
                        borderColor: 'rgba(52, 211, 153, 0.4)',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 185, 129, 0.22)',
                          borderColor: 'rgba(52, 211, 153, 0.6)',
                        },
                      },
                    }}
                  >
                    {preset.name}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Show All / Hide All */}
          <Stack direction="row" spacing={1} sx={{ '& > *': { flex: 1 } }}>
            <ButtonField
              style={{
                height: '28px',
                minWidth: '70px',
                borderRadius: '4px',
                fontSize: '12px',
              }}
              name={t('commonActionsShowAll')}
              onClick={onShowAll}
            />
            <ButtonField
              style={{
                height: '28px',
                minWidth: '70px',
                borderRadius: '4px',
                fontSize: '12px',
              }}
              name={t('commonActionsHideAll')}
              onClick={onHideAll}
            />
          </Stack>
        </Box>

        {/* Column checkboxes — main scrollable area */}
        <FormGroup
          sx={{
            px: 1,
            py: 0.75,
            flex: 1,
            minHeight: 0,
            flexWrap: 'nowrap',
            overflowY: 'auto',
            overflowX: 'hidden',
            '& .MuiFormControlLabel-root': {
              mx: 0,
              px: 1,
              py: 0.25,
              minHeight: 30,
              borderRadius: '4px',
              transition: 'background-color 0.15s',
              '&:hover': { backgroundColor: 'action.hover' },
            },
            '& .MuiFormControlLabel-label': {
              ml: 0.5,
            },
          }}
        >
          {filteredColumns.map((col) => {
            const isChecked =
              visibleColumns[col.field] !== undefined
                ? visibleColumns[col.field]
                : true
            const isDisabled = alwaysVisibleFields.includes(col.field)
            const cleanHeaderName = t(col.headerName)
              .replace(/\n/g, ' ')
              .replace(/\s*\([^)]*\)/g, '')
              .trim()

            return (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={isChecked || isDisabled}
                    onChange={() => onToggleColumn(col.field)}
                    disabled={isDisabled}
                    size="small"
                    checkedIcon={<CheckIcon />}
                    icon={<UnCheckIcon />}
                    sx={{
                      py: 0.5,
                      '&.Mui-disabled': { opacity: 0.4 },
                      '& .MuiSvgIcon-root': { fontSize: '18px' },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 400,
                      color: isDisabled ? 'rgba(0, 0, 0, 0.38)' : 'text.main',
                    }}
                  >
                    {cleanHeaderName}
                  </Typography>
                }
              />
            )
          })}
        </FormGroup>

        {/* Save Current View */}
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          {!showSaveInput ? (
            <Box sx={{ display: 'flex' }}>
              <ButtonField
                startIcon={<SaveIcon />}
                style={{
                  height: '28px',
                  minWidth: '100%',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
                name={t('commonActionsSaveCurrentView')}
                onClick={() => setShowSaveInput(true)}
                disabled={!canPersist}
              />
            </Box>
          ) : (
            <Stack spacing={1}>
              <TextField
                size="small"
                placeholder={t('commonViewNamePlaceholder')}
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveConfirm()
                  if (e.key === 'Escape') {
                    setViewName('')
                    setShowSaveInput(false)
                  }
                }}
                autoFocus
                sx={{
                  '& .MuiInputBase-input': { fontSize: '12px', py: 0.75 },
                }}
              />
              <Stack direction="row" spacing={1} sx={{ '& > *': { flex: 1 } }}>
                <ButtonField
                  startIcon={<SaveIcon />}
                  style={{
                    height: '28px',
                    minWidth: '70px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                  name="Save"
                  onClick={handleSaveConfirm}
                  disabled={!viewName.trim()}
                />
                <ButtonField
                  startIcon={<CancelIcon />}
                  style={{
                    height: '28px',
                    minWidth: '70px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                  name="Cancel"
                  onClick={() => {
                    setViewName('')
                    setShowSaveInput(false)
                  }}
                />
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>
    </Popover>
  )
}

export default ColumnSettingPopover
